import { useCallback } from 'react'
import { useSelector } from 'react-redux'

import { format, utcToZonedTime } from 'date-fns-tz'
import { fromJS, Map } from 'immutable'

import { createAppointmentPayload } from '@lyrahealth-inc/shared-app-logic/src/features/appointments/createAppointmentPayload'
import { getGuardianInfoForEpisode } from '@lyrahealth-inc/shared-app-logic/src/features/appointments/utils'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { Appointment } from '@lyrahealth-inc/shared-app-logic/src/models/appointment/Appointment'
import { Episode } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import { VIDEO_PLATFORM } from '@lyrahealth-inc/shared-app-logic/src/models/modality/Modality'

import { getAuthUserId, getAuthUserModalities } from '../../../../../data/auth/authSelectors'
import { useCreateAppointmentMutation, useCreateRecurringAppointmentMutation } from '../appointmentsApi'

export const useBookAppointment = () => {
  const [createAppointment] = useCreateAppointmentMutation()
  const [createRecurringAppointment] = useCreateRecurringAppointmentMutation()

  const userId = useSelector(getAuthUserId)
  const providerModalities = useSelector(getAuthUserModalities)
  const { isDAProviderMigrated, isAddNewClientsEnabled } = useFlags()

  return useCallback(
    async (
      event: {
        client: string
        startTime: string
        endTime: string
        slotEndTime: string
        localStartTime: string
        localEndTime: string
        localSlotEndTime: string
        duration: number
        meetingFormat: string
        episode: Episode
        recurring: boolean
        frequency: number
        repeatFor: number
        address?: string
        videoPlatform?: VIDEO_PLATFORM
        sessionType?: string
      },
      appointments: Appointment[],
    ) => {
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'America/Los_Angeles'
      const appointmentForEpisode = [...appointments].reverse().find((appt) => appt.episodeId === event.episode.id)
      // Historically, provider side appointment creation requires copying certain appointment fields from a previous appointment in the episode
      // Specifically in the case of a new client being added to the platform and booked for the first time by an engage provider,
      // they have an episode open without any prior appointments and exception must be made to fall back to defaults downstream.
      if (!appointmentForEpisode) {
        if (!isDAProviderMigrated || !isAddNewClientsEnabled) {
          throw new Error('No appointment found for episode')
        }
      }

      const appointmentMap = appointmentForEpisode ? fromJS(appointmentForEpisode) : Map()

      const date = utcToZonedTime(event.localStartTime, timeZone)
      const time = format(date, 'HH:mm:ss', { timeZone })
      const dateStr = format(date, 'yyyy-MM-dd', { timeZone })
      const newAppointment = createAppointmentPayload({
        appointment: Map({
          startDate: dateStr,
          startTime: time,
          timeZone,
          appointmentStatus: 'newAppt',
          appointmentDuration: event.duration,
          episodeId: event.episode.id,
          providerPatientId: event.episode.provider_patient_id,
          programId: event.episode.program_id,
          meetingFormat: event.meetingFormat,
          ...(event.sessionType && {
            sessionType: event.sessionType,
          }),
          ...(event.address && {
            address: event.address,
          }),
        }),
        providerID: userId,
        providerModalities,
        userID: event.client,
        referenceAppointment: appointmentMap,
      })

      // For bookings where we need to persist guardian info on the appointment
      // If any previous appointment has guardianLyraId, carry it over to the new appointment
      const appointmentsForEpisode = appointments.filter((appt) => appt.episodeId === event.episode.id)
      const guardianInfo = getGuardianInfoForEpisode(appointmentsForEpisode, event.episode?.participants)
      const shouldIncludeGuardianInfo = Boolean(guardianInfo.guardianLyraId)

      const fullPayload = {
        ...newAppointment,
        episodeId: event.episode.id,
        episodeState: event.episode.state,
        episodeEndDate: event.episode.end_date,
        providerPatientId: event.episode.provider_patient_id,
        programId: event.episode.program_id,
        ...(shouldIncludeGuardianInfo ? guardianInfo : {}),
        ...(event.videoPlatform && { videoPlatform: event.videoPlatform }),
      }
      if (event.recurring) {
        return await createRecurringAppointment({
          providerId: userId,
          data: {
            ...(event.sessionType && { sessionType: event.sessionType }),
            appointment: fullPayload,
            intervalDays: event.frequency,
            sessionCount: event.repeatFor,
          },
        }).unwrap()
      }
      return await createAppointment({
        providerId: userId,
        data: fullPayload,
      }).unwrap()
    },
    [
      userId,
      providerModalities,
      createAppointment,
      createRecurringAppointment,
      isDAProviderMigrated,
      isAddNewClientsEnabled,
    ],
  )
}
